import type { CurrentPlan, CustomPlanPricing, Plan } from "@circle-react/types";

import type { PlanTiers } from "@/react/helpers/planHelpers";
import {
  getNextTier,
  isPlanEnterprise,
  isPlanProfessional,
} from "@/react/helpers/planHelpers";

interface GetDefaultPlanTierParams {
  currentPlan?: CurrentPlan;
  customPlanOfferPlan?: CustomPlanPricing;
  hasActiveCommunityCoupon?: boolean;
  isCommunityOnPaidPlan: boolean;
  isCommunityOnTrial: boolean;
  isExpiredTrial: boolean;
  isICP: boolean;
  plans: Plan[];
}

// Falls back to the last-known tier, or the first plan, so a tier is always selected.
const fallbackPlanTier = (
  plans: Plan[],
  currentPlan?: CurrentPlan,
): PlanTiers | undefined => {
  const matchingPlan = plans.find(plan => plan.tier === currentPlan?.tier);
  return matchingPlan?.tier ?? plans[0]?.tier;
};

export const getDefaultPlanTier = ({
  plans,
  currentPlan,
  customPlanOfferPlan,
  isCommunityOnTrial,
  isExpiredTrial,
  isCommunityOnPaidPlan,
  isICP,
  hasActiveCommunityCoupon = false,
}: GetDefaultPlanTierParams): PlanTiers | undefined => {
  if (!plans.length) return undefined;

  const renewPlan = plans.find(plan => plan.plan_action === "renew");
  if (renewPlan) return renewPlan.tier;

  if (!!customPlanOfferPlan) return "plus";

  // Trial communities that signed up with a coupon are steered to the
  // coupon-discounted self-serve plans, not Plus.
  if (
    (isCommunityOnTrial || isExpiredTrial) &&
    isICP &&
    !hasActiveCommunityCoupon
  ) {
    return "plus";
  }

  if (isCommunityOnTrial || isExpiredTrial) {
    const professionalPlan = plans.find(plan => isPlanProfessional(plan.tier));
    if (professionalPlan) return professionalPlan.tier;
  }

  if (
    isCommunityOnPaidPlan &&
    currentPlan &&
    !isPlanEnterprise(currentPlan.tier)
  ) {
    const nextTierPlan = plans.find(
      plan => plan.tier === getNextTier(currentPlan.tier),
    );
    if (nextTierPlan) return nextTierPlan.tier;
  }

  return fallbackPlanTier(plans, currentPlan);
};
